import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { adminCreateCase } from "@/lib/api/admin-cases.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/cases/new")({
  head: () => ({
    meta: [
      { title: "Register a matter · NDLCS Admin" },
      { name: "description", content: "Register a new client matter in case management." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: NewCase,
});

const MATTER_TYPES = [
  "Labour dispute",
  "CCMA referral",
  "Contract drafting",
  "Contract review",
  "Legal opinion",
  "Debt collection",
  "POPIA request",
  "CIPC / company secretarial",
  "Compliance",
  "Immigration",
  "Consultation",
  "Other",
];

const DEPARTMENTS = ["Legal", "Labour", "Compliance", "Corporate", "Career services", "Education"];

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";

function NewCase() {
  const navigate = useNavigate();
  const create = useServerFn(adminCreateCase);
  const [form, setForm] = useState({
    title: "",
    category: "general",
    matter_type: "",
    department: "Legal",
    priority: "normal",
    assigned_to: "",
    due_date: "",
    notes: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    client_id_number: "",
    client_company: "",
    client_address: "",
    preferred_contact: "email",
    opposing_name: "",
    opposing_email: "",
    opposing_phone: "",
    opposing_representative: "",
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: () => create({ data: form as any }),
    onSuccess: (res: any) => {
      navigate({ to: "/portal/admin/cases/$id", params: { id: res.id } });
    },
  });

  return (
    <div className="space-y-5 py-4">
      <header>
        <h1 className="font-display text-2xl text-navy">Register a matter</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          A matter number is generated automatically in the format NDLCS-YYYY-TYPE-0001.
        </p>
      </header>

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <section className="space-y-3 rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-navy">Matter details</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="m-title" label="Matter title *">
              <input
                id="m-title"
                required
                className={inputCls}
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
              />
            </Field>
            <Field id="m-type" label="Matter type">
              <select
                id="m-type"
                className={inputCls}
                value={form.matter_type}
                onChange={(e) => set("matter_type", e.target.value)}
              >
                <option value="">Select…</option>
                {MATTER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="m-dept" label="Department">
              <select
                id="m-dept"
                className={inputCls}
                value={form.department}
                onChange={(e) => set("department", e.target.value)}
              >
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="m-priority" label="Priority">
              <select
                id="m-priority"
                className={inputCls}
                value={form.priority}
                onChange={(e) => set("priority", e.target.value)}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </Field>
            <Field id="m-assigned" label="Assigned to">
              <input
                id="m-assigned"
                className={inputCls}
                value={form.assigned_to}
                onChange={(e) => set("assigned_to", e.target.value)}
              />
            </Field>
            <Field id="m-due" label="Key due date">
              <input
                id="m-due"
                type="date"
                className={inputCls}
                value={form.due_date}
                onChange={(e) => set("due_date", e.target.value)}
              />
            </Field>
          </div>
          <Field id="m-notes" label="Summary of instructions">
            <textarea
              id="m-notes"
              rows={4}
              className={inputCls}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </Field>
        </section>

        <section className="space-y-3 rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-navy">Client information</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="c-name" label="Full name">
              <input
                id="c-name"
                className={inputCls}
                value={form.client_name}
                onChange={(e) => set("client_name", e.target.value)}
              />
            </Field>
            <Field id="c-email" label="Email *">
              <input
                id="c-email"
                type="email"
                required
                className={inputCls}
                value={form.client_email}
                onChange={(e) => set("client_email", e.target.value)}
              />
            </Field>
            <Field id="c-phone" label="Telephone">
              <input
                id="c-phone"
                className={inputCls}
                value={form.client_phone}
                onChange={(e) => set("client_phone", e.target.value)}
              />
            </Field>
            <Field id="c-id" label="ID / passport number">
              <input
                id="c-id"
                className={inputCls}
                value={form.client_id_number}
                onChange={(e) => set("client_id_number", e.target.value)}
              />
            </Field>
            <Field id="c-company" label="Company / employer">
              <input
                id="c-company"
                className={inputCls}
                value={form.client_company}
                onChange={(e) => set("client_company", e.target.value)}
              />
            </Field>
            <Field id="c-contact" label="Preferred contact method">
              <select
                id="c-contact"
                className={inputCls}
                value={form.preferred_contact}
                onChange={(e) => set("preferred_contact", e.target.value)}
              >
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="sms">SMS</option>
              </select>
            </Field>
          </div>
          <Field id="c-address" label="Address">
            <textarea
              id="c-address"
              rows={2}
              className={inputCls}
              value={form.client_address}
              onChange={(e) => set("client_address", e.target.value)}
            />
          </Field>
        </section>

        <section className="space-y-3 rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-navy">Opposing party (optional)</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="o-name" label="Name / entity">
              <input
                id="o-name"
                className={inputCls}
                value={form.opposing_name}
                onChange={(e) => set("opposing_name", e.target.value)}
              />
            </Field>
            <Field id="o-rep" label="Legal representative">
              <input
                id="o-rep"
                className={inputCls}
                value={form.opposing_representative}
                onChange={(e) => set("opposing_representative", e.target.value)}
              />
            </Field>
            <Field id="o-email" label="Email">
              <input
                id="o-email"
                className={inputCls}
                value={form.opposing_email}
                onChange={(e) => set("opposing_email", e.target.value)}
              />
            </Field>
            <Field id="o-phone" label="Telephone">
              <input
                id="o-phone"
                className={inputCls}
                value={form.opposing_phone}
                onChange={(e) => set("opposing_phone", e.target.value)}
              />
            </Field>
          </div>
        </section>

        {mutation.error ? (
          <p className="text-sm text-red-700">{String((mutation.error as any).message)}</p>
        ) : null}

        <button
          type="submit"
          disabled={mutation.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-medium text-white hover:bg-navy/90 disabled:opacity-60"
        >
          {mutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Register matter
        </button>
      </form>
    </div>
  );
}
